<template>
  <modal
    elem-id="modal_so_order_item_detail"
    size="large"
    :show="isVisible"
    :title="modalTitle"
    :save-label="''"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <!-- Action toolbar -->
      <div v-if="showToolbar" class="item-detail-toolbar">
        <Button
          v-if="canEdit"
          :label="t('Editar')"
          icon="pi pi-pencil"
          size="small"
          severity="secondary"
          outlined
          data-testid="so-item-detail-edit-btn"
          @click="handleEdit"
        />
        <Button
          v-if="canRemove"
          :label="t('Remover item')"
          icon="pi pi-trash"
          size="small"
          severity="danger"
          outlined
          data-testid="so-item-detail-remove-btn"
          @click="handleRemove"
        />
      </div>

      <Tabs v-model:value="activeTabIndex" :scrollable="false">
        <TabList>
          <Tab :value="0" data-testid="so-item-detail-tab-geral">{{ t('Geral') }}</Tab>
          <Tab v-if="!isProductItem" :value="1" data-testid="so-item-detail-tab-laudo-entrada">{{ t('Laudo Entrada') }}</Tab>
          <Tab v-if="!isProductItem" :value="2" data-testid="so-item-detail-tab-laudo-saida">{{ t('Laudo Saída') }}</Tab>
          <Tab v-if="!isProductItem" :value="3" data-testid="so-item-detail-tab-producao">{{ t('Produção') }}</Tab>
          <Tab v-if="!isProductItem" :value="5" data-testid="so-item-detail-tab-entrega">{{ t('Entrega') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel :value="0">
            <SOItemGeralTab ref="geralTabRef" :item="detailItem" :children="detailChildren" />
          </TabPanel>
          <TabPanel v-if="!isProductItem" :value="1">
            <SOItemLaudoEntradaTab ref="laudoEntradaTabRef" :item-id="currentItemId" />
          </TabPanel>
          <TabPanel v-if="!isProductItem" :value="2">
            <SOItemLaudoSaidaTab ref="laudoSaidaTabRef" :item-id="currentItemId" />
          </TabPanel>
          <TabPanel v-if="!isProductItem" :value="3">
            <SOItemProducaoTab ref="producaoTabRef" :item-id="currentItemId" />
          </TabPanel>
          <TabPanel v-if="!isProductItem" :value="5">
            <SOItemEntregaTab
              ref="entregaTabRef"
              :item-id="currentItemId"
              :current-delivery-date="detailItem?.so_order_item_expected_delivery_at ? new Date(detailItem.so_order_item_expected_delivery_at) : undefined"
              :delivered-at="detailItem?.so_order_item_delivered_at ?? null"
              :is-read-only="isEntregaReadOnly"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { useSOOrderItemDetail } from '../../../composables/useSOOrderItemDetail';
import { useCheckout } from '../../../composables/useCheckout';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import type { Orders_ItemRow } from '../../../contracts/Orders_ItemRow';
import type { Orders_ItemDetailResponseDados } from '../../../contracts/Orders_ItemDetailResponse';

const { t } = useI18n();
const { fetchItem } = useSOOrderItemDetail();
const { confirmAction } = useConfirmAction();

// Route removal through useCheckout so the received-floor guard applies here
// exactly as it does from the PDV checkout: useCheckout.removeItem surfaces the
// backend received-floor rejection verbatim via globalMessage.error. The modal
// does not preload the full order, so the advisory client-side projected-total
// check is a no-op here and the authoritative backend invariant is what blocks
// a floor-breaching removal (its error message is surfaced).
const checkoutOrderIdRef = ref<number | undefined>(undefined);
const { removeItem } = useCheckout(checkoutOrderIdRef);

interface TabComponent {
  loadData: () => void | Promise<void>;
}

interface ModalOpenPayload {
  itemId: number;
  initialTab?: 'geral' | 'laudo-entrada' | 'laudo-saida' | 'producao' | 'entrega';
}

const TAB_INDEX_MAP: Record<string, number> = {
  geral: 0,
  'laudo-entrada': 1,
  'laudo-saida': 2,
  producao: 3,
  entrega: 5,
};

const isVisible = ref(false);
const currentItemId = ref(0);
const currentServiceName = ref('');
const activeTabIndex = ref(0);

const geralTabRef = ref<TabComponent | null>(null);
const laudoEntradaTabRef = ref<TabComponent | null>(null);
const laudoSaidaTabRef = ref<TabComponent | null>(null);
const producaoTabRef = ref<TabComponent | null>(null);
const entregaTabRef = ref<TabComponent | null>(null);

const detailItem = ref<Orders_ItemRow | null>(null);
const detailChildren = ref<Orders_ItemRow[]>([]);
const detailWorkOrder = ref<any>(null);
const detailOrderStatus = ref<string | null>(null);

const isEntregaReadOnly = computed(() => {
  return !!detailItem.value?.so_order_item_delivered_at || detailOrderStatus.value === 'cancelled';
});

// A venda de um combo (NS 2026-0090) não tem laudo nem produção, como o produto,
// e não é um serviço: o wizard de serviço não a edita.
const isProductItem = computed(() =>
  detailItem.value?.so_order_item_type === 'product' || detailItem.value?.so_order_item_type === 'combo');

const showToolbar = computed(() => canEdit.value || canRemove.value);
const canEdit = computed(() => !isProductItem.value);
const canRemove = computed(() => true);

const modalTitle = computed(() => {
  let title = `${t('Item')} #${currentItemId.value}`;
  if (currentServiceName.value) {
    title += ` — ${currentServiceName.value}`;
  }
  return title;
});

// Lazy-load each tab when activated
watch(activeTabIndex, async (newIndex) => {
  await nextTick();
  switch (newIndex) {
    case 0:
      // Geral tab loads on open via handleModalOpen, but also supports re-fetch
      if (geralTabRef.value) {
        geralTabRef.value.loadData();
      }
      break;
    case 1:
      if (laudoEntradaTabRef.value) {
        laudoEntradaTabRef.value.loadData();
      }
      break;
    case 2:
      if (laudoSaidaTabRef.value) {
        laudoSaidaTabRef.value.loadData();
      }
      break;
    case 3:
      if (producaoTabRef.value) {
        producaoTabRef.value.loadData();
      }
      break;
    case 5:
      if (entregaTabRef.value) {
        entregaTabRef.value.loadData();
      }
      break;
  }
});

async function handleModalOpen(data?: ModalOpenPayload) {
  if (!data?.itemId) return;

  log('SOOrderItemDetailModal', 'handleModalOpen', 'Opening', data);

  currentItemId.value = Number(data.itemId);
  currentServiceName.value = '';
  isVisible.value = true;

  // Fetch the item immediately
  try {
    const result = await fetchItem(data.itemId) as Orders_ItemDetailResponseDados | undefined;
    if (result) {
      const item = result.item as Orders_ItemRow | null;
      detailItem.value = item;
      detailChildren.value = (result.children as Orders_ItemRow[]) || [];
      detailWorkOrder.value = result.work_order;
      detailOrderStatus.value = result.order_status;
      currentServiceName.value = item?.so_service_name || '';
    }
  } catch (err) {
    log('SOOrderItemDetailModal', 'handleModalOpen', 'Error fetching item', err);
  }

  // Set initial tab
  if (data.initialTab && TAB_INDEX_MAP[data.initialTab] !== undefined) {
    activeTabIndex.value = TAB_INDEX_MAP[data.initialTab];
  } else {
    activeTabIndex.value = 0;
  }

  // Load Geral tab data immediately (notify the component)
  nextTick(() => {
    if (geralTabRef.value) {
      geralTabRef.value.loadData();
    }
  });
}

function handleClose() {
  isVisible.value = false;
  currentItemId.value = 0;
  currentServiceName.value = '';
}

function handleEdit(): void {
  if (!detailItem.value) return;
  eventBus.emit('modal.pdv-servico-wizard.open', {
    orderId: detailItem.value.so_order_item_order_id,
    franchiseId: detailItem.value.so_order_item_franchise_id,
    editItem: detailItem.value,
    children: detailChildren.value || [],
  });
  handleClose();
}

function handleRemove(): void {
  confirmAction({
    message: t('Tem certeza que deseja remover este item da OS?'),
    header: t('Confirmar Remoção'),
    acceptLabel: t('Sim, remover'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        await removeItem(Number(currentItemId.value));
        eventBus.emit('globalMessage.success', t('Item removido'));
        eventBus.emit('so-order-item.removed', { itemId: currentItemId.value });
        handleClose();
      } catch (err) {
        // Error handled by composable
      }
    },
  });
}

onMounted(() => {
  eventBus.on('modal.so-order-item.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-order-item.open', handleModalOpen);
});
</script>

<style scoped>
.item-detail-toolbar {
  display: flex;
  gap: 0.5rem;
  padding: 0 0 0.75rem 0;
  flex-wrap: wrap;
}
</style>
